/**
 * SunoStemJobsContext — фоновое стем-разделение (App-level).
 *
 * Зеркало SunoMidiJobsContext, но результат — набор аудио-дорожек
 * (вокал/бас/драмы/инструментал…), не JSON. Стемы это результат-группа
 * исходного трека: на бэке живут в request.result_urls, доступ через
 * GET /tracks/:id/stems. Отдельными библиотечными треками НЕ дублируются.
 *
 * Особенность провайдеров:
 *   - Mureka: POST /stem-separation СИНХРОННЫЙ (блокируется до готовности) →
 *     сразу после ответа стемы доступны через /stems.
 *   - Suno: асинхронный → поллим /stems пока не отдаст дорожки.
 * Поэтому после старта пробуем /stems сразу, иначе — поллинг.
 *
 * На готовность диспатчится window-event `suno-stems-ready` — список треков
 * перезапрашивает /tracks, чтобы бейдж STEMS появился на строке.
 */
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import {
	stemSeparation,
	getTrackStems,
	type StemKind,
	type StemsGroup,
	type SunoTrack,
} from '../api/music'
import { getTaskStatus, TASK_GONE_MESSAGE } from '../api/images'
import { showToast } from '../components/Toast'
import { translateSunoError } from '../pages/generate/utils/sunoErrors'
import { pollUntil } from '../pages/generate/utils/waitForTask'

const LS_PENDING_KEY = 'suno_stems_pending_v1'
const POLL_INTERVAL_MS = 10_000
// Запуск без номера задачи (сохранён до выкатки 27.09) спросить о провале не о чем — ждём его ограниченно.
const LEGACY_POLL_MAX_ATTEMPTS = 60 // 10 минут
const LEGACY_PENDING_MAX_AGE_MS = 30 * 60 * 1000

interface PendingStemJob {
	trackId: string
	title: string | null
	/** Вид разбора — чтобы после reload дожидаться именно его. */
	kind?: StemKind
	/** Номер задачи: по нему видно провал (стемы на треке его не показывают). */
	requestId?: string
	startedAt: number
}

interface StemsData {
	urls: string[]
	labels: string[]
}

export interface StemsViewerState {
	trackId: string
	title: string | null
	urls: string[]
	labels: string[]
	/** Все виды разбора трека (полный + минусовка): вьюер показывает оба,
	 *  чтобы оплаченный ранее результат не прятался за новым. */
	groups?: StemsGroup[]
}

/** Что именно запускаем: полный разбор или минусовку (голос + минус). */
export interface StemStartOptions {
	kind?: StemKind
}

interface SunoStemJobsValue {
	start: (track: SunoTrack, opts?: StemStartOptions) => void
	/** Идёт ли операция. Без вида — «любая»; с видом — именно она: «Дорожки» и
	 *  «Минусовка» разные кнопки и не должны гасить друг друга. */
	isRunning: (trackId: string, kind?: StemKind) => boolean
	getCached: (trackId: string) => StemsData | null
	pendingViewer: StemsViewerState | null
	dismissViewer: () => void
	/** Открыть вьюер, подтянув дорожки из /stems (клик по бейджу STEMS). */
	openFromBackend: (trackId: string, title: string | null) => Promise<boolean>
	/** Открыть готовый результат ИМЕННО этого вида, а если его нет — запустить.
	 *  Так юзер не платит второй раз за то, что уже сделано, и не получает
	 *  чужой вид разбора вместо запрошенного. */
	openOrStart: (track: SunoTrack, kind: StemKind) => Promise<void>
}

const SunoStemJobsContext = createContext<SunoStemJobsValue | null>(null)

function loadPending(): Record<string, PendingStemJob> {
	try {
		const raw = localStorage.getItem(LS_PENDING_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		if (!parsed || typeof parsed !== 'object') return {}
		const now = Date.now()
		const filtered: Record<string, PendingStemJob> = {}
		for (const [k, v] of Object.entries(parsed as Record<string, PendingStemJob>)) {
			// С номером задачи запуск живёт, пока сервер не ответил (план «Ждём ответа нейросети», Ф3).
			if (v && typeof v === 'object' && v.startedAt && (v.requestId || now - v.startedAt < LEGACY_PENDING_MAX_AGE_MS)) {
				filtered[k] = v
			}
		}
		return filtered
	} catch {
		return {}
	}
}
function savePending(pending: Record<string, PendingStemJob>) {
	try {
		localStorage.setItem(LS_PENDING_KEY, JSON.stringify(pending))
	} catch {
		// localStorage может быть отключён — pending не критичен
	}
}

export function SunoStemJobsProvider({ children }: { children: ReactNode }) {
	const [cache, setCache] = useState<Record<string, StemsData>>({})
	const [runningIds, setRunningIds] = useState<Set<string>>(
		() => new Set(Object.keys(loadPending())),
	)
	const [pendingViewer, setPendingViewer] = useState<StemsViewerState | null>(null)
	const activeRef = useRef<Set<string>>(new Set())
	const pendingRef = useRef<Record<string, PendingStemJob>>(loadPending())

	const markRunning = useCallback((trackId: string, kind: StemKind, running: boolean) => {
		setRunningIds((prev) => {
			const next = new Set(prev)
			const key = `${trackId}:${kind}`
			if (running) next.add(key)
			else next.delete(key)
			return next
		})
	}, [])

	const setPendingJob = useCallback((trackId: string, job: PendingStemJob | null) => {
		if (job) pendingRef.current[trackId] = job
		else delete pendingRef.current[trackId]
		savePending({ ...pendingRef.current })
	}, [])

	// Кэш по (трек + вид разбора): минусовка и полный разбор — разные наборы,
	// общий ключ отдавал бы на второй запуск результат первого.
	const cacheKey = (trackId: string, kind: StemKind) => `${trackId}:${kind}`

	const finish = useCallback(
		(
			trackId: string,
			title: string | null,
			data: StemsData,
			announce: boolean,
			kind: StemKind,
			groups?: StemsGroup[],
		) => {
			setCache((prev) => ({ ...prev, [cacheKey(trackId, kind)]: data }))
			setPendingViewer({ trackId, title, urls: data.urls, labels: data.labels, groups })
			if (announce) {
				showToast(kind === 'minus' ? 'Минусовка готова' : 'Стемы готовы', 'success')
				window.dispatchEvent(new CustomEvent('suno-stems-ready', { detail: { trackId } }))
			}
		},
		[],
	)

	const runJob = useCallback(
		async (trackId: string, title: string | null, kickoff: boolean, kind: StemKind = 'stems') => {
			const jobKey = `${trackId}:${kind}`
			if (activeRef.current.has(jobKey)) return
			activeRef.current.add(jobKey)
			markRunning(trackId, kind, true)
			const startedAt = pendingRef.current[jobKey]?.startedAt ?? Date.now()
			let requestId = pendingRef.current[jobKey]?.requestId
			setPendingJob(jobKey, { trackId, title, kind, requestId, startedAt })
			try {
				// kickoff=false → resume после reload: операция уже запущена, только поллим.
				if (kickoff) {
					const res = await stemSeparation({ track_id: trackId, stem_kind: kind })
					if (!res.ok) {
						showToast(translateSunoError(res.error, 'stems'), 'error')
						return
					}
					requestId = res.request_id
					setPendingJob(jobKey, { trackId, title, kind, requestId, startedAt })
				}
				// Готовые стемы — на треке; провал — только в статусе задачи. Ждём ответа сервера без потолка
				// (план «Ждём ответа нейросети», Ф3), а запуск без номера задачи — ограниченно (LEGACY_*).
				const stemsNow = async () => {
					const s = await getTrackStems(trackId)
					// Ждём именно СВОЙ вид разбора: иначе уже готовый прошлый
					// результат мгновенно «завершил» бы новую операцию.
					const mine = s.groups?.find((g) => g.kind === kind)
					const urls = mine?.result_urls ?? (s.groups ? [] : s.result_urls || [])
					return s.ok && urls.length > 0 ? { urls, labels: mine?.labels ?? s.labels ?? [], groups: s.groups } : null
				}
				type Outcome = { stems: NonNullable<Awaited<ReturnType<typeof stemsNow>>> } | { error: string } | { still: true }
				let completedSeen = 0
				let legacyTries = 0
				// Mureka синхронна → /stems готовы сразу, без ожидания.
				const first = await stemsNow()
				const outcome: Outcome | null = first
					? { stems: first }
					: await pollUntil<Outcome>(
						async () => {
							const ready = await stemsNow()
							if (ready) return { stems: ready }
							if (!requestId) return ++legacyTries >= LEGACY_POLL_MAX_ATTEMPTS ? { still: true } : undefined
							const t = await getTaskStatus(requestId)
							if (t.status === 'failed') {
								return { error: translateSunoError(t.error_message_ru || t.error_message, 'stems') }
							}
							// Задача готова, а стемов на треке нет и на следующем круге — ответ без результата.
							if (t.status === 'completed' && ++completedSeen >= 2) {
								return { error: 'Suno завершил разбор без дорожек — попробуй ещё раз.' }
							}
							return undefined
						},
						{ minDelayMs: POLL_INTERVAL_MS, startedAt, onGone: () => ({ error: TASK_GONE_MESSAGE }) },
					)
				if (outcome && 'stems' in outcome) {
					const { urls, labels, groups } = outcome.stems
					finish(trackId, title, { urls, labels }, true, kind, groups)
				} else if (outcome && 'error' in outcome) {
					showToast(outcome.error, 'error')
				} else if (outcome) {
					showToast(
						kind === 'minus'
							? 'Минусовка ещё готовится — появится у трека, когда будет готова.'
							: 'Стемы ещё готовятся — появятся у трека, когда будут готовы.',
						'info',
					)
				}
			} catch (e) {
				showToast(e instanceof Error ? e.message : 'Ошибка стем-разделения', 'error')
			} finally {
				activeRef.current.delete(jobKey)
				markRunning(trackId, kind, false)
				setPendingJob(jobKey, null)
			}
		},
		[finish, markRunning, setPendingJob],
	)

	const start = useCallback(
		(track: SunoTrack, opts?: StemStartOptions) => {
			const trackId = track.id
			const kind: StemKind = opts?.kind ?? 'stems'
			const cached = cache[cacheKey(trackId, kind)]
			if (cached) {
				setPendingViewer({ trackId, title: track.title, urls: cached.urls, labels: cached.labels })
				return
			}
			if (activeRef.current.has(`${trackId}:${kind}`)) {
				showToast(kind === 'minus' ? 'Минусовка уже готовится...' : 'Стем-разделение уже идёт...', 'info')
				return
			}
			showToast(
				kind === 'minus'
					? `Достаём минус из «${track.title?.trim() || 'трека'}»...`
					: `Разделяем «${track.title?.trim() || 'трек'}» на стемы...`,
				'info',
			)
			void runJob(trackId, track.title, true, kind)
		},
		[cache, runJob],
	)

	const openFromBackend = useCallback(
		async (trackId: string, title: string | null): Promise<boolean> => {
			// Ходим на бэк даже при живом кэше: там оба вида разбора, а в кэше
			// лежит только тот, что делали в этой сессии. Кэш — запасной путь.
			const s = await getTrackStems(trackId)
			if (!s.ok || !(s.result_urls?.length)) {
				const cached = cache[cacheKey(trackId, 'stems')] ?? cache[cacheKey(trackId, 'minus')]
				if (!cached) return false
				setPendingViewer({ trackId, title, urls: cached.urls, labels: cached.labels })
				return true
			}
			const data = { urls: s.result_urls, labels: s.labels || [] }
			const kind: StemKind = s.groups?.[0]?.kind ?? 'stems'
			setCache((prev) => ({ ...prev, [cacheKey(trackId, kind)]: data }))
			setPendingViewer({ trackId, title, urls: data.urls, labels: data.labels, groups: s.groups })
			return true
		},
		[cache],
	)

	const openOrStart = useCallback(
		async (track: SunoTrack, kind: StemKind): Promise<void> => {
			const cached = cache[cacheKey(track.id, kind)]
			if (cached) {
				setPendingViewer({ trackId: track.id, title: track.title, urls: cached.urls, labels: cached.labels })
				return
			}
			// Бэк уже сказал в списке, есть ли результат этого вида. Если явно
			// «нет» — не ходим за ним впустую: запрос всё равно вернул бы 404, а
			// в консоли это выглядит ошибкой. undefined (старый ответ без флага)
			// — спрашиваем, как раньше. Сделанное в этой сессии ловит кэш выше.
			const known = kind === 'minus' ? track.has_minus : track.has_stems
			if (known === false) {
				start(track, { kind })
				return
			}
			const s = await getTrackStems(track.id)
			const mine = s.ok ? s.groups?.find((g) => g.kind === kind) : undefined
			if (mine?.result_urls?.length) {
				setCache((prev) => ({
					...prev,
					[cacheKey(track.id, kind)]: { urls: mine.result_urls, labels: mine.labels || [] },
				}))
				setPendingViewer({
					trackId: track.id,
					title: track.title,
					urls: mine.result_urls,
					labels: mine.labels || [],
					groups: s.groups,
				})
				return
			}
			// Старый бэк без groups: единственный набор считаем полным разбором —
			// ровно так же, как трактует его и сам бэк у записей без метки.
			if (!s.groups && kind === 'stems' && s.ok && s.result_urls?.length) {
				setPendingViewer({
					trackId: track.id,
					title: track.title,
					urls: s.result_urls,
					labels: s.labels || [],
				})
				return
			}
			start(track, { kind })
		},
		[cache, start],
	)

	const getCached = useCallback(
		(trackId: string): StemsData | null =>
			cache[cacheKey(trackId, 'stems')] ?? cache[cacheKey(trackId, 'minus')] ?? null,
		[cache],
	)
	const dismissViewer = useCallback(() => setPendingViewer(null), [])
	const isRunning = useCallback(
		(trackId: string, kind?: StemKind) =>
			kind
				? runningIds.has(`${trackId}:${kind}`)
				: runningIds.has(`${trackId}:stems`) || runningIds.has(`${trackId}:minus`),
		[runningIds],
	)

	// Resume pending-джобов после reload (только поллинг, без повторного старта).
	// Ключ теперь составной («трек:вид»), поэтому id берём ИЗ САМОЙ записи, а не
	// из ключа — иначе в поллинг ушёл бы «abc:minus» вместо id трека.
	useEffect(() => {
		const pending = pendingRef.current
		const jobs = Object.values(pending)
		if (jobs.length === 0) return
		const t = setTimeout(() => {
			for (const job of jobs) {
				if (job?.trackId) void runJob(job.trackId, job.title, false, job.kind ?? 'stems')
			}
		}, 500)
		return () => clearTimeout(t)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const value = useMemo<SunoStemJobsValue>(
		() => ({ start, isRunning, getCached, pendingViewer, dismissViewer, openFromBackend, openOrStart }),
		[start, isRunning, getCached, pendingViewer, dismissViewer, openFromBackend, openOrStart],
	)

	return <SunoStemJobsContext.Provider value={value}>{children}</SunoStemJobsContext.Provider>
}

// Хук живёт рядом с провайдером — как во всех наших контекстах; выносить его
// в отдельный файл ради fast-refresh не станем (сломает импорты по проекту).
// eslint-disable-next-line react-refresh/only-export-components
export function useSunoStemJobs(): SunoStemJobsValue {
	const ctx = useContext(SunoStemJobsContext)
	if (!ctx) {
		return {
			start: () => {},
			isRunning: () => false,
			getCached: () => null,
			pendingViewer: null,
			dismissViewer: () => {},
			openFromBackend: async () => false,
			openOrStart: async () => {},
		}
	}
	return ctx
}
